import moment from "moment";

function extractYmd(value: string | Date | null | undefined) {
	if (!value) return "";
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) return "";
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, "0");
		const day = String(value.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	}

	const text = String(value).trim();
	const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;

	const dmy = text.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
	if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;

	const parsed = moment(text);
	return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function formatDateInTimeZone(date: Date, timeZoneId?: string | null) {
	const options: Intl.DateTimeFormatOptions = {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		timeZone: timeZoneId || undefined,
	};
	return new Intl.DateTimeFormat("en-GB", options).format(date).replace(/\//g, "-");
}

function isCalendarLikeString(value: string) {
	const text = value.trim();
	return /^\d{4}-\d{2}-\d{2}/.test(text) || /^\d{1,2}-\d{1,2}-\d{4}/.test(text);
}

export function formatOrderCalendarDate(
	value: string | Date | null | undefined,
	timeZoneId?: string | null,
) {
	if (typeof value === "string") {
		const text = value.trim();
		const isDateOnlyYmd = /^\d{4}-\d{2}-\d{2}$/.test(text);
		const isDateOnlyDmy = /^\d{1,2}-\d{1,2}-\d{4}$/.test(text);
		if (!isDateOnlyYmd && !isDateOnlyDmy) {
			const date = new Date(text);
			if (!Number.isNaN(date.getTime())) {
				try {
					return formatDateInTimeZone(date, timeZoneId);
				} catch {
					return formatDateInTimeZone(date);
				}
			}
		}
	}

	if (value instanceof Date) {
		try {
			return formatDateInTimeZone(value, timeZoneId);
		} catch {
			return formatDateInTimeZone(value);
		}
	}

	const ymd = extractYmd(value);
	if (!ymd) return "";
	const [year, month, day] = ymd.split("-");
	return `${day}-${month}-${year}`;
}

export function formatOrderCalendarDateLegacyGuard(
	primaryValue: string | Date | null | undefined,
	fallbackValue: string | Date | null | undefined,
	timeZoneId?: string | null,
) {
	if (typeof primaryValue === "string" && primaryValue.trim()) {
		// Legacy records often store an authoritative calendar string for pickup/dropoff.
		// Keep that day stable instead of re-interpreting via timezone conversion.
		if (isCalendarLikeString(primaryValue)) {
			return formatOrderCalendarDate(primaryValue);
		}
		return formatOrderCalendarDate(primaryValue, timeZoneId);
	}
	if (primaryValue instanceof Date) {
		return formatOrderCalendarDate(primaryValue, timeZoneId);
	}
	return formatOrderCalendarDate(fallbackValue, timeZoneId);
}

export function toOrderCalendarDateInput(value: string | Date | null | undefined) {
	return extractYmd(value);
}

export function orderCalendarDateToPickerDate(value: string | Date | null | undefined) {
	const ymd = extractYmd(value);
	return ymd ? moment(ymd, "YYYY-MM-DD").toDate() : null;
}
